import React, { useState, useEffect } from "react";
import secureAxios from "../../utils/secureAxios";
import { useAuth } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";

const ConsumerNotes = ({ consumerId, consumerName }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [isPrivate, setIsPrivate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const apiBase = getApiBaseURL();

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/consumer-notes/consumer/${consumerId}`
      );
      setNotes(res.data);
    } catch (err) {
      console.error("Failed to load notes:", err);
      alert("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (consumerId) {
      loadNotes();
    }
  }, [consumerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newNote.trim()) {
      alert("Please enter note text");
      return;
    }

    try {
      await secureAxios.post(`${apiBase}/api/consumer-notes`, {
        consumer_id: consumerId,
        note_text: newNote,
        note_type: noteType,
        is_private: isPrivate,
      });

      setNewNote("");
      setNoteType("general");
      setIsPrivate(false);
      loadNotes();
    } catch (err) {
      console.error("Error creating note:", err);
      alert("Failed to create note");
    }
  };

  const handleEdit = (note) => {
    setEditingNoteId(note.note_id);
    setEditText(note.note_text);
  };

  const handleUpdate = async (note_id) => {
    try {
      await secureAxios.put(`${apiBase}/api/consumer-notes/${note_id}`, {
        note_text: editText,
      });
      setEditingNoteId(null);
      setEditText("");
      loadNotes();
    } catch (err) {
      console.error("Error updating note:", err);
      alert("Failed to update note");
    }
  };

  const handleDelete = async (note_id) => {
    if (!window.confirm("Are you sure you want to delete this note?")) {
      return;
    }

    try {
      await secureAxios.delete(`${apiBase}/api/consumer-notes/${note_id}`);
      loadNotes();
    } catch (err) {
      console.error("Error deleting note:", err);
      alert("Failed to delete note");
    }
  };

  const togglePin = async (note) => {
    try {
      await secureAxios.put(`${apiBase}/api/consumer-notes/${note.note_id}`, {
        is_pinned: !note.is_pinned,
      });
      loadNotes();
    } catch (err) {
      console.error("Error toggling pin:", err);
    }
  };

  const getNoteTypeColor = (type) => {
    const colors = {
      general: "secondary",
      progress: "success",
      incident: "warning",
      medical: "danger",
      administrative: "info",
    };
    return colors[type] || "secondary";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="consumer-notes">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>
          <i className="bi bi-journal-text me-2"></i>
          Consumer Notes
          <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
        </h5>
      </div>

      {/* Add Note Form - ALWAYS VISIBLE */}
      <form onSubmit={handleSubmit} className="card mb-3">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Add New Note</label>
            <textarea
              className="form-control"
              rows="3"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter note about consumer..."
              required
            />
          </div>

          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">Note Type</label>
              <select
                className="form-select form-select-sm"
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
              >
                <option value="general">General</option>
                <option value="progress">Progress Update</option>
                <option value="incident">Incident</option>
                <option value="medical">Medical</option>
                <option value="administrative">Administrative</option>
              </select>
            </div>

            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isPrivate"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="isPrivate">
                  <i className="bi bi-lock me-1"></i>
                  Private Note
                </label>
              </div>
            </div>

            <div className="col-md-4 d-flex align-items-end justify-content-end">
              <button type="submit" className="btn btn-primary btn-sm">
                <i className="bi bi-plus-circle me-1"></i>
                Add Note
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* COLLAPSIBLE NOTES LIST SECTION */}
      <div className="card">
        <div className="card-header bg-primary text-white">
          <button
            className="btn btn-link text-white text-decoration-none d-flex justify-content-between align-items-center w-100 p-0"
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ fontSize: "1.1rem", fontWeight: "500" }}
          >
            <span>
              <i
                className={`bi bi-chevron-${
                  isExpanded ? "down" : "right"
                } me-2`}
              ></i>
              Previous Notes
              {notes.length > 0 && (
                <span
                  className="badge bg-light text-primary ms-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  {notes.length} {notes.length === 1 ? "note" : "notes"}
                </span>
              )}
            </span>
            <i
              className={`bi bi-chevron-${isExpanded ? "up" : "down"}`}
              style={{ fontSize: "1.3rem" }}
            ></i>
          </button>
        </div>

        {isExpanded && (
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : notes.length === 0 ? (
              <div className="alert alert-info mb-0">
                <i className="bi bi-info-circle me-2"></i>
                No notes yet. Add your first note above.
              </div>
            ) : (
              <div className="notes-list">
                {notes.map((note) => (
                  <div
                    key={note.note_id}
                    className={`card mb-2 ${
                      note.is_pinned ? "border-warning" : ""
                    }`}
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <span
                            className={`badge bg-${getNoteTypeColor(
                              note.note_type
                            )} me-2`}
                          >
                            {note.note_type}
                          </span>
                          {note.is_private && (
                            <span className="badge bg-dark me-2">
                              <i className="bi bi-lock"></i> Private
                            </span>
                          )}
                          {note.is_pinned && (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-pin-angle-fill"></i> Pinned
                            </span>
                          )}
                        </div>

                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => togglePin(note)}
                            title={note.is_pinned ? "Unpin" : "Pin"}
                          >
                            <i
                              className={`bi bi-pin${
                                note.is_pinned ? "-fill" : ""
                              }`}
                            ></i>
                          </button>

                          {(user.user_id === note.user_id ||
                            user.role === "admin") && (
                            <>
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleEdit(note)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(note.note_id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingNoteId === note.note_id ? (
                        <div>
                          <textarea
                            className="form-control mb-2"
                            rows="3"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                          />
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleUpdate(note.note_id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditText("");
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <p className="mb-2">{note.note_text}</p>
                      )}

                      <div className="text-muted small">
                        <i className="bi bi-person-circle me-1"></i>
                        <strong>{note.created_by_name}</strong>
                        {note.created_by_role && (
                          <span className="ms-1">({note.created_by_role})</span>
                        )}
                        <span className="mx-2">•</span>
                        <i className="bi bi-clock me-1"></i>
                        {formatDate(note.created_at)}
                        {note.edited_at && (
                          <>
                            <span className="mx-2">•</span>
                            <i className="bi bi-pencil me-1"></i>
                            Edited by {note.edited_by_name} on{" "}
                            {formatDate(note.edited_at)}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumerNotes;
